<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import PaginateWithCount from '@/components/PaginateWithCount.vue';
import CopyElement from '@/components/CopyElement.vue';
import MileageCorrectionService from '@/services/models/MileageCorrectionService';
import i18n from '@/lang/i18n';
import type DeliveryPlan from '@/types/DeliveryPlan';

const props = defineProps<{
  trips: DeliveryPlan[];
  loading: boolean;
  totalPage: number;
  itemsPerPage: number;
  allElementsCount: number;
  currentPage: number;
  correctionBaseId: string | null;
  correctionOutOfServiceMeter: number | null;
}>();

const emit = defineEmits<{
  'update:currentPage': [value: number];
  'update:correctionBaseId': [value: string | null];
  'update:correctionOutOfServiceMeter': [value: number | null];
}>();

const editingId = ref<string | null>(null);
const editingOutOfServiceMeter = ref<string>('');
const editingError = ref<string>('');

const currentPageModel = computed({
  get: () => props.currentPage,
  set: (value) => emit('update:currentPage', value),
});

const tripsSortedByLeavingTime = computed(() =>
  [...props.trips].sort(
    (a, b) =>
      new Date(a.leaving_warehouse_date_time).getTime() -
      new Date(b.leaving_warehouse_date_time).getTime()
  )
);

// Map<delivery_plan_id, 修正後出庫時メーター(km)>
const correctedMeters = computed<Map<string, number>>(() => {
  if (props.correctionBaseId === null || props.correctionOutOfServiceMeter === null) {
    return new Map();
  }

  const sorted = tripsSortedByLeavingTime.value;
  const baseIndex = sorted.findIndex(op => op.delivery_plan_id === props.correctionBaseId);
  if (baseIndex === -1) {
    return new Map();
  }

  const result = new Map<string, number>();
  let runningOutOfServiceMeter = props.correctionOutOfServiceMeter;

  for (let i = baseIndex; i < sorted.length; i++) {
    result.set(sorted[i].delivery_plan_id, runningOutOfServiceMeter);
    runningOutOfServiceMeter = Math.round((runningOutOfServiceMeter + Number(sorted[i].total_mileage ?? 0)) * 10) / 10;
  }

  return result;
});

function isCorrected(item: DeliveryPlan): boolean {
  return MileageCorrectionService.isCorrected(item, correctedMeters.value);
}

function getPrevReturnMeter(item: DeliveryPlan): number | null {
  return MileageCorrectionService.getPrevReturnMeter(item, tripsSortedByLeavingTime.value, correctedMeters.value);
}

function applyPrevReturnMeter(item: DeliveryPlan) {
  const value = getPrevReturnMeter(item);
  if (value === null) {
    return;
  }
  emit('update:correctionBaseId', item.delivery_plan_id);
  emit('update:correctionOutOfServiceMeter', value);
}

function startEditing(item: DeliveryPlan) {
  editingId.value = item.delivery_plan_id;
  const preset = props.correctionBaseId === item.delivery_plan_id && props.correctionOutOfServiceMeter !== null
    ? props.correctionOutOfServiceMeter
    : item.out_of_service_meter;
  editingOutOfServiceMeter.value = preset != null ? String(preset) : '';

  nextTick(() => {
    const input = document.querySelector<HTMLInputElement>('.editing-meter-input input');
    input?.focus();
    input?.select();
  });
}

function confirmEditing() {
  if (editingId.value === null) {
    return;
  }
  const error = MileageCorrectionService.validateOutOfServiceMeter(editingOutOfServiceMeter.value);
  if (error) {
    editingError.value = error;
    return;
  }
  emit('update:correctionBaseId', editingId.value);
  emit('update:correctionOutOfServiceMeter', parseFloat(editingOutOfServiceMeter.value));
  resetEditing();
}

function resetEditing() {
  editingId.value = null;
  editingOutOfServiceMeter.value = '';
  editingError.value = '';
}

watch(() => props.trips, resetEditing);
</script>

<template>
  <div v-if="props.loading" class="d-flex justify-content-center py-4">
    <v-progress-circular indeterminate color="primary" />
  </div>
  <div v-else class="table-scrollbar mb-2">
    <v-table>
      <thead>
        <tr class="table-header-row">
          <th class="table-edge-left">{{ i18n.global.t('pages.mileageCorrection.tripId') }}</th>
          <th>{{ i18n.global.t('pages.mileageCorrection.leavingDatetime') }}</th>
          <th>{{ i18n.global.t('pages.mileageCorrection.returningDatetime') }}</th>
          <th class="text-right">{{ i18n.global.t('pages.mileageCorrection.outOfServiceMeter') }}</th>
          <th class="text-right">{{ i18n.global.t('pages.mileageCorrection.totalMileage') }}</th>
          <th class="text-right">{{ i18n.global.t('pages.mileageCorrection.returnMeter') }}</th>
          <th class="text-right table-edge-right">{{ i18n.global.t('pages.mileageCorrection.diff') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-if="props.trips.length > 0">
          <tr
            v-for="item in props.trips"
            :key="item.delivery_plan_id"
            :class="{
              'correction-base-row': props.correctionBaseId === item.delivery_plan_id,
              'correction-affected-row': isCorrected(item) && props.correctionBaseId !== item.delivery_plan_id,
            }"
          >
            <td class="table-edge-left">
              <div class="d-flex align-items-center">
                <span>{{ item.delivery_plan_id.slice(-7) }}</span>
                <CopyElement :copyText="item.delivery_plan_id" size="small" class="ml-1" />
              </div>
            </td>
            <td>{{ MileageCorrectionService.toDateTime(item.leaving_warehouse_date_time) }}</td>
            <td>{{ MileageCorrectionService.toDateTime(item.returning_warehouse_date_time) }}</td>
            <td class="meter-cell text-right">
              <!-- 編集中 -->
              <div v-if="editingId === item.delivery_plan_id" class="d-flex align-items-center editing-meter-input">
                <v-text-field
                  v-model="editingOutOfServiceMeter"
                  type="number"
                  density="compact"
                  :hide-details="!editingError"
                  :error-messages="editingError"
                  suffix="km"
                  class="meter-input"
                  @keydown.enter="confirmEditing"
                  @keydown.esc="resetEditing"
                />
                <v-btn
                  size="x-small"
                  variant="text"
                  icon="mdi-check"
                  color="success"
                  class="ml-1"
                  @click="confirmEditing"
                />
                <v-btn
                  size="x-small"
                  variant="text"
                  icon="mdi-close"
                  class="ml-1"
                  @click="resetEditing"
                />
              </div>
              <!-- 表示中 -->
              <div v-else class="d-inline-flex align-items-center">
                <span class="meter-value" :class="{ 'corrected-base-value': isCorrected(item) }">
                  {{ MileageCorrectionService.displayOutOfServiceMeter(item, correctedMeters) }}
                </span>
                <v-btn
                  size="x-small"
                  variant="text"
                  icon="mdi-pencil"
                  @click="startEditing(item)"
                />
                <v-btn
                  v-if="getPrevReturnMeter(item) !== null"
                  size="x-small"
                  variant="text"
                  icon="mdi-auto-fix"
                  color="primary"
                  :title="i18n.global.t('pages.mileageCorrection.applyPrevBtn')"
                  @click="applyPrevReturnMeter(item)"
                />
              </div>
            </td>
            <td class="text-right">
              {{ MileageCorrectionService.displayTotalMileage(item) }}
            </td>
            <td class="text-right" :class="{ 'corrected-base-value': isCorrected(item) }">
              {{ MileageCorrectionService.displayReturnMeter(item, correctedMeters) }}
            </td>
            <td class="text-right table-edge-right">
              <span :class="{ 'corrected-value': isCorrected(item) }">
                {{ MileageCorrectionService.getDiff(item, correctedMeters, tripsSortedByLeavingTime) }}
              </span>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td colspan="7">{{ i18n.global.t('pages.mileageCorrection.noData') }}</td>
          </tr>
        </template>
      </tbody>
    </v-table>
  </div>

  <PaginateWithCount
    class="justify-end"
    :totalPage="props.totalPage"
    v-model="currentPageModel"
    :itemsPerPage="props.itemsPerPage"
    :allElementsCount="props.allElementsCount"
    :showItemCount="true"
  />
</template>

<style scoped>
:deep(.v-table__wrapper) {
  max-height: 60vh;
  overflow-y: auto;
}
:deep(table th) {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 1;
}
.table-edge-left {
  padding-left: 20px !important;
}
.table-edge-right {
  padding-right: 20px !important;
}
.correction-base-row {
  background-color: #fff8e1;
}
.correction-affected-row {
  background-color: #f3f8ff;
}
.corrected-value {
  font-weight: bold;
  color: #e65100;
}
.corrected-base-value {
  font-weight: bold;
  color: #e65100;
}
.meter-cell {
  min-width: 180px;
}
.meter-value {
  display: inline-block;
  min-width: 90px;
}
.meter-input {
  width: 140px;
}
</style>
